import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ProfileAvatarUpload } from "@/components/profile-avatar-upload";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash2,
  MapPin,
  DollarSign,
  Calendar as CalendarIcon,
  User,
  CheckCircle,
  Loader2,
  ExternalLink,
  XCircle,
} from "lucide-react";
import type { TeacherProfile, TeacherAddress } from "@contracts/teacher";
import { apiUrl } from "@/lib/api";

const COMMON_CURRENCIES = [
  "USD", "EUR", "GBP", "UAH", "PLN", "CZK", "CHF", "CAD", "AUD",
  "JPY", "CNY", "KRW", "BRL", "INR", "RUB", "TRY", "SEK", "NOK", "DKK",
];

interface GoogleCalendarInfo {
  id: string;
  summary: string;
  primary: boolean;
}

export default function SettingsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: profile, isLoading } = useQuery<TeacherProfile>({
    queryKey: ["/api/profile"],
  });

  const { data: calendarStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["/api/calendar/status"],
  });

  const { data: calendars = [], isLoading: calendarsLoading } = useQuery<GoogleCalendarInfo[]>({
    queryKey: ["/api/calendar/list"],
    enabled: !!calendarStatus?.connected,
  });

  const [addresses, setAddresses] = useState<TeacherAddress[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [newAddressLabel, setNewAddressLabel] = useState("");
  const [newAddressValue, setNewAddressValue] = useState("");
  const [newCurrency, setNewCurrency] = useState("");

  useEffect(() => {
    if (profile) {
      setAddresses(profile.addresses || []);
      setCurrencies(profile.currencies || []);
    }
  }, [profile]);

  const params = new URLSearchParams(window.location.search);
  useEffect(() => {
    if (params.get("google") === "connected") {
      toast({ title: "Google Calendar connected successfully", variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/status"] });
      window.history.replaceState({}, "", "/settings");
    } else if (params.get("google") === "error") {
      toast({ title: "Failed to connect Google Calendar", variant: "destructive" });
      window.history.replaceState({}, "", "/settings");
    }
  }, []);

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<TeacherProfile>) => {
      const res = await apiRequest("PUT", "/api/profile", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      toast({ title: "Settings saved", variant: "success" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const disconnectGoogleMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/google/disconnect");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/status"] });
      toast({ title: "Google Calendar disconnected", variant: "success" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const uploadAvatarMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("avatar", file);
      const res = await fetch(apiUrl("/api/profile/avatar"), {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: "Profile picture updated", variant: "success" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function addAddress() {
    if (!newAddressLabel.trim() || !newAddressValue.trim()) return;
    const newAddr: TeacherAddress = {
      id: crypto.randomUUID(),
      label: newAddressLabel.trim(),
      address: newAddressValue.trim(),
    };
    const updated = [...addresses, newAddr];
    setAddresses(updated);
    setNewAddressLabel("");
    setNewAddressValue("");
    updateMutation.mutate({ addresses: updated });
  }

  function removeAddress(id: string) {
    const updated = addresses.filter(a => a.id !== id);
    setAddresses(updated);
    updateMutation.mutate({ addresses: updated });
  }

  function addCurrency() {
    const c = newCurrency.trim().toUpperCase();
    if (!c || currencies.includes(c)) return;
    const updated = [...currencies, c];
    setCurrencies(updated);
    setNewCurrency("");
    updateMutation.mutate({ currencies: updated });
  }

  function removeCurrency(code: string) {
    const updated = currencies.filter(c => c !== code);
    setCurrencies(updated);
    updateMutation.mutate({ currencies: updated });
  }

  function addCommonCurrency(code: string) {
    if (currencies.includes(code)) return;
    const updated = [...currencies, code];
    setCurrencies(updated);
    updateMutation.mutate({ currencies: updated });
  }

  function selectCalendar(calendarId: string) {
    updateMutation.mutate({
      googleCalendarId: calendarId,
    });
  }

  function handleConnectGoogle() {
    window.location.href = apiUrl(
      "/api/auth/google?returnTo=" + encodeURIComponent("/settings")
    );
  }

  const initials = user
    ? `${(user.firstName || "")[0] || ""}${(user.lastName || "")[0] || ""}`.toUpperCase() || "T"
    : "T";

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  const isGoogleConnected = !!profile?.googleCalendarConnected && calendarStatus?.connected;
  const selectedCalendar = calendars.find(c => c.id === profile?.googleCalendarId);

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-3xl mx-auto" data-testid="page-settings">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight" data-testid="text-page-title">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your profile and preferences</p>
      </div>

      <Card className="p-4 sm:p-6" data-testid="card-profile">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-semibold">Profile</h2>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <ProfileAvatarUpload
            imageUrl={user?.profileImageUrl}
            initials={initials}
            onCroppedFile={(file) => uploadAvatarMutation.mutateAsync(file)}
            isUploading={uploadAvatarMutation.isPending}
          />
          <div className="text-center sm:text-left">
            <p className="font-medium text-lg" data-testid="text-profile-name">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-sm text-muted-foreground" data-testid="text-profile-email">{user?.email}</p>
            <p className="text-xs text-muted-foreground mt-1">Click the avatar to change your profile picture</p>
          </div>
        </div>
      </Card>

      <Card className="p-4 sm:p-6" data-testid="card-addresses">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-semibold">Teaching Addresses</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Add locations where you provide offline lessons. These can be selected when adding students.
        </p>

        {addresses.length > 0 && (
          <div className="space-y-2 mb-4">
            {addresses.map(addr => (
              <div key={addr.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30" data-testid={`address-${addr.id}`}>
                <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 hidden sm:block" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{addr.label}</p>
                  <p className="text-xs text-muted-foreground truncate">{addr.address}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeAddress(addr.id)} data-testid={`button-remove-address-${addr.id}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="Label (e.g. Home, Office)"
            value={newAddressLabel}
            onChange={(e) => setNewAddressLabel(e.target.value)}
            className="sm:flex-1 sm:min-w-[140px]"
            data-testid="input-address-label"
          />
          <Input
            placeholder="Full address"
            value={newAddressValue}
            onChange={(e) => setNewAddressValue(e.target.value)}
            className="sm:flex-[2] sm:min-w-[200px]"
            data-testid="input-address-value"
          />
          <Button
            onClick={addAddress}
            disabled={!newAddressLabel.trim() || !newAddressValue.trim()}
            className="w-full sm:w-auto"
            data-testid="button-add-address"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </Card>

      <Card className="p-4 sm:p-6" data-testid="card-currencies">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-semibold">Currencies</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Define which currencies you accept for payments. These appear when setting student rates.
        </p>

        {currencies.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {currencies.map(code => (
              <Badge key={code} variant="secondary" className="gap-1 py-1.5 px-3" data-testid={`currency-${code}`}>
                {code}
                <button onClick={() => removeCurrency(code)} className="ml-1 hover:text-destructive" data-testid={`button-remove-currency-${code}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-4">
          <Input
            placeholder="Currency code (e.g. USD)"
            value={newCurrency}
            onChange={(e) => setNewCurrency(e.target.value.toUpperCase())}
            className="flex-1 max-w-xs"
            maxLength={3}
            data-testid="input-currency"
          />
          <Button
            onClick={addCurrency}
            disabled={!newCurrency.trim() || currencies.includes(newCurrency.trim().toUpperCase())}
            data-testid="button-add-currency"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>

        <div>
          <p className="text-xs text-muted-foreground mb-2">Quick add:</p>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_CURRENCIES.filter(c => !currencies.includes(c)).slice(0, 10).map(code => (
              <Button
                key={code}
                variant="outline"
                size="sm"
                onClick={() => addCommonCurrency(code)}
                data-testid={`button-quick-add-${code}`}
              >
                {code}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      <Card className="p-4 sm:p-6" data-testid="card-google-calendar">
        <div className="flex items-center gap-2 mb-4">
          <CalendarIcon className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-semibold">Google Calendar</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Connect your Google Calendar to automatically create events for scheduled lessons.
        </p>

        {isGoogleConnected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="default" className="bg-green-600" data-testid="badge-calendar-connected">
                <CheckCircle className="w-3 h-3 mr-1" />
                Connected
              </Badge>
              <span className="text-sm text-muted-foreground">Your Google Calendar is linked</span>
            </div>

            {profile?.googleCalendarId && selectedCalendar ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <CalendarIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Syncing to: {selectedCalendar.summary}</p>
                    <p className="text-xs text-muted-foreground">
                      New lessons will automatically appear in this calendar
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select
                    value={profile.googleCalendarId || ""}
                    onValueChange={(val) => selectCalendar(val)}
                  >
                    <SelectTrigger className="flex-1" data-testid="select-calendar">
                      <SelectValue placeholder="Change calendar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {calendars.map(cal => (
                        <SelectItem key={cal.id} value={cal.id!}>
                          {cal.summary} {cal.primary ? "(Primary)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={() => disconnectGoogleMutation.mutate()}
                    disabled={disconnectGoogleMutation.isPending}
                    data-testid="button-disconnect-calendar"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Disconnect
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm">
                  Choose a calendar to sync your lessons to:
                </p>
                {calendarsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading calendars...
                  </div>
                ) : calendars.length > 0 ? (
                  <div className="space-y-2">
                    {calendars.map(cal => (
                      <div
                        key={cal.id}
                        className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => selectCalendar(cal.id!)}
                        data-testid={`calendar-option-${cal.id}`}
                      >
                        <CalendarIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{cal.summary}</p>
                          {cal.primary && (
                            <p className="text-xs text-muted-foreground">Primary calendar</p>
                          )}
                        </div>
                        <Button size="sm" data-testid={`button-select-calendar-${cal.id}`}>
                          Select
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No calendars found</p>
                )}
                <Button
                  variant="outline"
                  onClick={() => disconnectGoogleMutation.mutate()}
                  disabled={disconnectGoogleMutation.isPending}
                  data-testid="button-disconnect-calendar"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Disconnect
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" data-testid="badge-calendar-disconnected">Not Connected</Badge>
              <span className="text-sm text-muted-foreground">Sign in with Google to sync lessons</span>
            </div>
            <Button
              onClick={handleConnectGoogle}
              className="w-full sm:w-auto"
              data-testid="button-connect-google"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Connect Google Calendar
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
